import React, { useState, forwardRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const Input = forwardRef(({
  label,
  type = 'text',
  placeholder,
  value,
  onChange,
  onBlur,
  onFocus,
  error,
  helperText,
  required = false,
  disabled = false,
  fullWidth = false,
  size = 'md',
  icon,
  suffix,
  className = '',
  containerClassName = '',
  ...props
}, ref) => {
  const [focused, setFocused] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const baseClasses = 'block w-full rounded-xl border bg-white/95 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-slate-900/90';
  
  const sizes = {
    sm: 'min-h-[40px] px-3 py-2 text-sm',
    md: 'min-h-[46px] px-4 py-2.5 text-sm sm:text-base',
    lg: 'min-h-[52px] px-5 py-3 text-base sm:text-lg'
  };
  
  const stateClasses = error
    ? 'border-red-300 text-slate-900 placeholder:text-slate-400 focus:border-red-500 focus:ring-red-500 dark:border-red-500/50 dark:text-white'
    : focused
    ? 'border-blue-500 text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:ring-blue-500 dark:text-white'
    : 'border-gray-300 text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:ring-blue-500 dark:border-slate-700 dark:text-white';
  
  const inputClasses = `${baseClasses} ${sizes[size]} ${stateClasses} ${icon ? 'pl-10' : ''} ${suffix || type === 'password' ? 'pr-10' : ''} ${className}`;
  const containerClasses = `${fullWidth ? 'w-full' : ''} ${containerClassName}`;
  
  const handleFocus = (e) => {
    setFocused(true);
    onFocus?.(e);
  };
  
  const handleBlur = (e) => {
    setFocused(false);
    onBlur?.(e);
  };
  
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };
  
  return (
    <div className={containerClasses}>
      {label && (
        <motion.label
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`block text-sm font-medium mb-2 ${
            error ? 'text-red-700 dark:text-red-300' : 'text-gray-700 dark:text-slate-200'
          }`}
        >
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </motion.label>
      )}
      
      <div className="relative">
        {icon && (
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <div className={`h-5 w-5 ${error ? 'text-red-400' : 'text-gray-400'}`}>
              {icon}
            </div>
          </div>
        )}
        
        <motion.input
          ref={ref}
          type={type === 'password' && showPassword ? 'text' : type}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={disabled}
          required={required}
          className={inputClasses}
          whileFocus={{ scale: 1.01 }}
          {...props}
        />
        
        {(suffix || type === 'password') && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
            {type === 'password' ? (
              <button
                type="button"
                onClick={togglePasswordVisibility}
                className="text-gray-400 hover:text-gray-600 focus:outline-none focus:text-gray-600 dark:hover:text-slate-200 dark:focus:text-slate-200"
              >
                {showPassword ? (
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3.156 3.156m6.722 6.722L21.844 21.844" />
                  </svg>
                ) : (
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            ) : (
              <div className="text-gray-400">{suffix}</div>
            )}
          </div>
        )}
      </div>
      
      <AnimatePresence>
        {(error || helperText) && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-1"
          >
            <p className={`text-sm ${error ? 'text-red-600 dark:text-red-300' : 'text-gray-600 dark:text-slate-400'}`}>
              {error || helperText}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});

Input.displayName = 'Input';

export default Input;
export { Input };
